import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { WerkzoekenFeedContentDto, WerkzoekenFeedJobDto } from "./types";
import { jobSyncQueryKeys } from "../query-keys";

import { instance } from "@/lib/axios/instance";

export function useWerkzoekenFeedContent(
  feedUrl: string | null | undefined
): UseQueryResult<WerkzoekenFeedContentDto, AxiosError> {
  return useQuery({
    queryKey: jobSyncQueryKeys.feedContentByUrl("werkzoeken", feedUrl),
    queryFn: async () => {
      if (!feedUrl) {
        throw new Error("Feed URL is required");
      }

      // Extract the path from the feed URL
      // feedUrl can be either:
      // - Full URL: https://domain.com/api/v2.0/werkzoeken/JobSync/{clientId}
      // - Relative path: /api/v2.0/werkzoeken/JobSync/{clientId}
      let feedPath: string;
      try {
        // Try parsing as full URL first
        const url = new URL(feedUrl);
        feedPath = url.pathname;
      } catch {
        // If parsing fails, assume it's already a relative path
        feedPath = feedUrl.startsWith("/") ? feedUrl : `/${feedUrl}`;
      }

      // Use the actual feed URL from registration to fetch the feed
      // This ensures we're showing what's actually in the feed
      const response = await instance.get<string>(
        feedPath,
        {
          responseType: "text",
        }
      );

      // Parse XML to extract job information
      const parser = new DOMParser();
      const doc = parser.parseFromString(response.data, "text/xml");
      const jobs: WerkzoekenFeedJobDto[] = [];

      const jobElements = doc.getElementsByTagName("job");
      for (let i = 0; i < jobElements.length; i++) {
        const jobElement = jobElements[i];
        jobs.push({
          id: jobElement.getElementsByTagName("id")[0]?.textContent || "",
          title: jobElement.getElementsByTagName("title")[0]?.textContent || null,
          company: jobElement.getElementsByTagName("company")[0]?.textContent || null,
          city: jobElement.getElementsByTagName("city")[0]?.textContent || null,
          url: jobElement.getElementsByTagName("url")[0]?.textContent || null,
        });
      }

      return {
        totalJobs: jobs.length,
        jobs,
      };
    },
    enabled: !!feedUrl,
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
  });
}
